/* /_systeme — the design system reviewed in isolation (Step 2 gallery).
   Every component, every state, both themes via the toggle. Dev/review
   surface: linked from nowhere, excluded from the product nav on purpose. */
import { useState } from 'react'
import {
  Button, Checkbox, EmptyState, ErrorState, Field, Icon, Input, KV, Kbd,
  ListRow, Modal, Note, Pill, Rule, Select, Skeleton, Steps, Switch, Textarea,
  Tile, Tiles, useToast,
} from '@/ui'
import { useEvents, useSupports } from '@/lib/api'

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 34 }}>
      <p className="t-eyebrow">{id}</p>
      <h2 className="t-section" style={{ margin: '6px 0 14px' }}>{title}</h2>
      {children}
    </section>
  )
}

export default function Systeme() {
  const [modal, setModal] = useState(false)
  const toast = useToast()

  return (
    <div style={{ maxWidth: 860 }}>
      <h1 className="t-display">Le système, en isolation</h1>
      <p className="t-body" style={{ marginTop: 8 }}>
        F-1…F-12 en composants réels sur les tokens <code className="t-code">--bmr-*</code>.
        Chaque écran du produit se compose d’ici — jamais de CSS ponctuel.
      </p>

      <Section id="F-2" title="Gamme typographique">
        <p className="t-display">Un cadeau vous attend.</p>
        <p className="t-section">Titre de section — Lora 500</p>
        <p className="t-page">Titre de page — Jakarta 800</p>
        <p className="t-body">Corps de texte — 65ch max, Moss. Les accents français rendent correctement : éèêàçù, et l’euro : 12,50 €.</p>
        <p className="t-code">CODE-4K7T · IBM Plex Mono</p>
        <p className="t-kpi">1 284 €</p>
      </Section>

      <Section id="F-3" title="Boutons — le survol assombrit">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="primary">Publier la campagne</Button>
          <Button>Annuler</Button>
          <Button variant="danger">Supprimer</Button>
          <Button variant="ghost">Voir le détail</Button>
          <Button variant="primary" size="sm">Petit</Button>
          <Button variant="primary" disabled>Indisponible</Button>
        </div>
        <p className="f-help" style={{ marginTop: 8 }}>
          Survol : <code className="t-code">--bmr-action-hover</code> (7,70:1) — jamais Leaf Bright (règle du 29/08).
        </p>
      </Section>

      <Section id="F-5" title="Pastilles d’état — le mot voyage avec la couleur">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Pill tone="ok">Active</Pill>
          <Pill tone="done">Terminée</Pill>
          <Pill tone="warn">À tester</Pill>
          <Pill tone="crit">Refusée</Pill>
          <Pill tone="spark">Cadeau</Pill>
        </div>
      </Section>

      <Section id="F-4" title="Formulaires & note de candeur">
        <div style={{ maxWidth: 380 }}>
          <Field label="Nom de la campagne" help="Visible uniquement par votre équipe.">
            {id => <Input id={id} defaultValue="Rentrée 2026" />}
          </Field>
          <Field label="E-mail" error="Cette adresse ne semble pas valide.">
            {id => <Input id={id} defaultValue="lea@exemple" />}
          </Field>
          <Field label="Établissement">
            {id => <Select id={id}><option>Valessio — Paris 9</option><option>Marais</option></Select>}
          </Field>
          <Field label="Message (facultatif)">
            {id => <Textarea id={id} placeholder="Votre message…" />}
          </Field>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '14px 0' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <Switch defaultChecked /> Un cadeau à la fois
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <Checkbox defaultChecked /> J’accepte les conditions
            </label>
          </div>
          <Note tone="candour"><b>Désactiver&nbsp;:</b> vos clients pourront cumuler — votre encours augmentera.</Note>
          <div style={{ height: 8 }} />
          <Note tone="ok">Le cadeau est déjà gagné. Il s’ouvre à la fin du compte à rebours, avis ou pas.</Note>
          <div style={{ height: 8 }} />
          <Note tone="crit">Cette action retire le support de la circulation immédiatement.</Note>
        </div>
      </Section>

      <Section id="F-6" title="Tuiles métriques">
        <Tiles>
          <Tile hero label="Ce mois-ci" value="37 clients revenus" hint="412 € de cadeaux utilisés" />
          <Tile label="Participations" value="218" hint="30 j · quota le 1 sept." />
          <Tile label="Note Google" value="4,6 → 4,7" hint="instantané · contexte" />
          <Tile label="Encours" value="1 284 €" hint="183 cadeaux actifs" />
        </Tiles>
      </Section>

      <Section id="F-12" title="Lignes de liste">
        <ListRow title="Rentrée 2026" meta="Paris 9 · v3 publiée · testée le 29 août"
          end={<><Pill tone="ok">Active</Pill><Button size="sm">Mettre en pause</Button></>} />
        <ListRow title="Stock de cadeaux bas — « Café offert »" meta="14 restants · environ 4 jours au rythme actuel"
          end={<Button size="sm">Réapprovisionner</Button>} />
      </Section>

      <Section id="F-7" title="Table">
        <div className="tablewrap">
          <table className="tbl">
            <thead><tr><th>Client</th><th>Étape</th><th>Cadeaux</th><th>Dernière visite</th><th>État</th></tr></thead>
            <tbody>
              <tr><td><b>Léa M.</b></td><td>3 · Surprise</td><td>1 actif</td><td>29 août</td><td><Pill tone="ok">Actif</Pill></td></tr>
              <tr><td><b>Sofia B.</b></td><td>5 · VIP</td><td>0</td><td>12 août</td><td><Pill tone="done">Utilisé</Pill></td></tr>
              <tr><td><b>Marc D.</b></td><td>1</td><td>1 expiré</td><td>2 juil.</td><td><Pill tone="warn">Expiré</Pill></td></tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="F-9" title="Vide, chargement, erreur">
        <EmptyState
          title="Vos clients apparaîtront ici dès la première participation."
          why="C’est vide parce que personne n’a encore scanné votre support."
          action={<><Button variant="primary" size="sm">Imprimer mon support</Button><Button size="sm">Tester le parcours</Button></>}>
          <p>Chaque personne qui scanne et joue est enregistrée ici — c’est ce qui vous permet de voir qui revient.</p>
        </EmptyState>
        <div style={{ display: 'grid', gap: 8, maxWidth: 320, margin: '14px 0' }}>
          <Skeleton /><Skeleton width="72%" /><Skeleton width="45%" />
        </div>
        <ErrorState what="L’envoi n’est pas parti."
          lost="Votre brouillon est conservé — rien n’a été perdu."
          action={<Button variant="primary" size="sm">Réessayer</Button>} />
      </Section>

      <Section id="F-11 · F-12" title="Modale, toast, étapes, divers">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button onClick={() => setModal(true)}>Ouvrir la confirmation</Button>
          <Button onClick={() => toast({ message: 'Campagne archivée.', undo: () => {} })}>Archiver (toast + annuler)</Button>
        </div>
        <div style={{ margin: '16px 0' }}>
          <Steps items={['Objectif', 'Moment', 'Expérience', 'Style']} current={2} />
        </div>
        <Rule>ou</Rule>
        <div style={{ maxWidth: 340 }}>
          <KV rows={[['Portée', 'cet établissement'], ['Compte à rebours', 'préréglage 30 s · plafond 120 s'], ['Raccourci', <Kbd key="k">/</Kbd>]]} />
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
          {['home', 'campaigns', 'rewards', 'customers', 'analytics', 'settings'].map(n =>
            <Icon key={n} name={n} size={18} />)}
          <span className="f-help">icônes duotone — currentColor + fill token</span>
        </div>
        <Modal open={modal} title="Mettre « Rentrée 2026 » en pause ?" onClose={() => setModal(false)}
          actions={<><Button onClick={() => setModal(false)}>Annuler</Button>
            <Button variant="primary" onClick={() => setModal(false)}>Mettre en pause</Button></>}>
          <Note tone="ok">Les cadeaux déjà gagnés restent valables et utilisables à la caisse.</Note>
        </Modal>
      </Section>

      <Section id="§J · seam" title="Displays & événements (attribution)">
        <SeamJ />
      </Section>
    </div>
  )
}

/* §J review block — proves the NOW-constraints on the seam: every display
   carries its permanent token (go./d/{token}) and its access methods; every
   participation-side event carries display_id + access_method. Changes with
   the world switcher like everything else. */
function SeamJ() {
  const { data: displays } = useSupports()
  const { data: events } = useEvents()
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div>
        <p className="t-eyebrow" style={{ margin: '0 0 6px' }}>Displays enregistrés</p>
        {(displays ?? []).map(d => (
          <ListRow key={d.id} title={d.name}
            meta={<span className="t-code" style={{ wordBreak: 'break-all' }}>go.buildmyreviews.app/d/{d.token}</span>}
            end={<span style={{ display: 'flex', gap: 6 }}>
              {d.methods.map(m => <Pill key={m} tone="ink">{m.toUpperCase()}</Pill>)}
            </span>} />
        ))}
        {(displays ?? []).length === 0 && <p className="f-help">Aucun display — monde « jour 1 ».</p>}
      </div>
      <div>
        <p className="t-eyebrow" style={{ margin: '0 0 6px' }}>Événements — display_id + access_method</p>
        {(events ?? []).map(e => (
          <ListRow key={e.id} title={e.label} meta={`${e.at} · ${e.kind}`}
            end={e.displayId
              ? <span className="t-code">{e.displayId} · {e.accessMethod}</span>
              : <span className="f-help">sans surface d’entrée</span>} />
        ))}
        {(events ?? []).length === 0 && <p className="f-help">Aucun événement — monde « jour 1 ».</p>}
      </div>
      <p className="f-help">Modèle : qr · nfc · kiosk · web. Le QR imprimé encode le jeton permanent — jamais une destination codée en dur ; kiosk/web arrivent avec le module Displays.</p>
    </div>
  )
}
